import { useState, useEffect } from 'react';
import { Plus, Pencil, Save, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { Role } from '@/types';
import { api } from '@/lib/api';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';

const RESOURCES = ['marketing_plans', 'marketing_activities', 'campaigns', 'suppliers', 'teams', 'statistics', 'users', 'roles', 'settings', 'integrations'] as const;
const ACTIONS = ['view', 'edit', 'delete', 'export'] as const;
type Resource = (typeof RESOURCES)[number];
type Action = (typeof ACTIONS)[number];

const RESOURCE_LABELS: Record<Resource, string> = {
  marketing_plans: 'Marketing-Pläne',
  marketing_activities: 'Aktivitäten',
  campaigns: 'Kampagnen',
  suppliers: 'Lieferanten',
  teams: 'Teams',
  statistics: 'Statistiken',
  users: 'Benutzer',
  roles: 'Rollen',
  settings: 'Einstellungen',
  integrations: 'Integrationen',
};

const ACTION_LABELS: Record<Action, string> = {
  view: 'Ansehen',
  edit: 'Bearbeiten',
  delete: 'Löschen',
  export: 'Export',
};

function roleMatrix(role: Role): Record<string, boolean> {
  return Object.fromEntries(RESOURCES.flatMap((resource) => ACTIONS.map((action) => [
    `${resource}:${action}`,
    role.permissions?.some((permission) => permission.resource === resource && permission.action === action && permission.is_active) ?? false,
  ])));
}

export function RolesAdminPage() {
  const confirmDialog = useConfirmDialog();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [matrix, setMatrix] = useState<Record<string, Record<string, boolean>>>({});
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');

  const loadRoles = () => {
    setLoading(true);
    setError(null);
    api
      .get<{ success?: boolean; data?: Role[]; error?: string }>('/admin/roles')
      .then((res) => {
        if (res.success === false || !Array.isArray(res.data)) throw new Error(res.error || 'Rollen konnten nicht geladen werden.');
        setRoles(res.data);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Rollen konnten nicht geladen werden.');
        setRoles([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadRoles();
  }, []);

  useEffect(() => {
    setMatrix(Object.fromEntries(roles.map((role) => [role.id, roleMatrix(role)])));
  }, [roles]);

  const cancelEditing = (role: Role) => {
    setMatrix((previous) => ({ ...previous, [role.id]: roleMatrix(role) }));
    setEditingRoleId(null);
  };

  const getPerm = (roleId: string, resource: Resource, action: Action) => matrix[roleId]?.[`${resource}:${action}`] ?? false;
  const setPerm = (roleId: string, resource: Resource, action: Action, value: boolean) => {
    setMatrix((m) => ({
      ...m,
      [roleId]: {
        ...(m[roleId] ?? {}),
        [`${resource}:${action}`]: value,
      },
    }));
  };

  const saveRolePermissions = async (roleId: string) => {
    // The API replaces the whole permission set; keep permissions that this
    // matrix does not expose (for example social_posts and documents).
    const perms: { resource: string; action: Action }[] = (roles.find((role) => role.id === roleId)?.permissions ?? [])
      .filter((permission) => permission.is_active && !(RESOURCES as readonly string[]).includes(permission.resource))
      .map(({ resource, action }) => ({ resource, action }));
    RESOURCES.forEach((res: any) => {
      ACTIONS.forEach((act) => {
        if (getPerm(roleId, res, act)) perms.push({ resource: res, action: act });
      });
    });
    try {
      await api.post(`/admin/roles/${roleId}/permissions`, { permissions: perms });
      setEditingRoleId(null);
      loadRoles();
    } catch (e) {
      setError((e as Error).message ?? 'Berechtigungen konnten nicht gespeichert werden.');
    }
  };

  const deleteRole = async (role: Role) => {
    if (role.is_system) return;
    const confirmed = await confirmDialog({
      title: `Rolle "${role.name}" löschen?`,
      description: 'Diese Aktion kann nicht rückgängig gemacht werden.',
      confirmText: 'Löschen',
      cancelText: 'Abbrechen',
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await api.delete(`/admin/roles/${role.id}`);
      loadRoles();
    } catch (e) {
      setError((e as Error).message ?? 'Rolle konnte nicht gelöscht werden.');
    }
  };

  const createRole = async () => {
    if (!newRoleName.trim()) return;
    try {
      await api.post('/admin/roles', { name: newRoleName.trim(), description: newRoleDesc.trim(), is_system: false });
      setCreateDialogOpen(false);
      setNewRoleName('');
      setNewRoleDesc('');
      loadRoles();
    } catch (e) {
      setError((e as Error).message ?? 'Rolle konnte nicht erstellt werden.');
    }
  };

  if (loading && roles.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-border border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="max-w-3xl space-y-2">
          <h1 className="text-2xl font-bold text-foreground">Rollen & Berechtigungen</h1>
          <p className="text-sm leading-relaxed text-muted">
            Rollen bündeln Rechte auf Ressourcen (Pläne, Kampagnen, Admin …). Systemrollen sind geschützt; eigene Rollen nur
            anlegen, wenn Sie ein feingranulares Modell brauchen. Änderungen wirken sofort für alle zugewiesenen Nutzer.
          </p>
        </div>
        <Button className="shrink-0" onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Eigene Rolle anlegen
        </Button>
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          {error}
          <Button variant="outline" size="sm" className="ml-3" onClick={loadRoles} disabled={loading}>Erneut laden</Button>
        </div>
      )}

      <div className="space-y-6">
        {roles.map((role) => (
          <Card key={role.id} className="border-border bg-card">
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {role.name}
                    {role.is_system && <Badge variant="secondary">System</Badge>}
                  </CardTitle>
                  {role.description && <p className="text-sm text-muted mt-1">{role.description}</p>}
                </div>
                {editingRoleId === role.id ? (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => cancelEditing(role)}>Abbrechen</Button>
                    <Button size="sm" onClick={() => saveRolePermissions(role.id)}>
                      <Save className="mr-2 h-4 w-4" />
                      Speichern
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditingRoleId(role.id)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Bearbeiten
                    </Button>
                    {!role.is_system && (
                      <Button variant="destructive" size="sm" onClick={() => deleteRole(role)}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Löschen
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 pr-4 font-medium text-muted w-40">Ressource</th>
                      {ACTIONS.map((act) => (
                        <th key={act} className="text-center py-2 px-2 font-medium text-muted">{ACTION_LABELS[act]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {RESOURCES.map((res) => (
                      <tr key={res} className="border-b border-border">
                        <td className="py-2 pr-4 font-medium text-muted">{RESOURCE_LABELS[res]}</td>
                        {ACTIONS.map((act) => (
                          <td key={act} className="text-center py-2 px-2">
                            <input
                              type="checkbox"
                              aria-label={`${role.name}: ${RESOURCE_LABELS[res]} – ${ACTION_LABELS[act]}`}
                              checked={getPerm(role.id, res, act)}
                              disabled={editingRoleId !== role.id}
                              onChange={(e) => setPerm(role.id, res, act, e.target.checked)}
                              className="h-4 w-4 rounded border-border text-foreground focus:ring-border"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neue Rolle anlegen</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Name</Label>
              <Input placeholder="z. B. Kampagnen-Manager" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>Beschreibung (optional)</Label>
              <Input placeholder="Kurzbeschreibung" value={newRoleDesc} onChange={(e) => setNewRoleDesc(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              <X className="h-4 w-4" />
              Abbrechen
            </Button>
            <Button onClick={createRole}>
              <Plus className="h-4 w-4" />
              Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
